import { useState, memo } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, RefreshCcw } from "lucide-react";
import {
  getGetInteractiveMaterialQueryKey,
  useCloneInteractiveMaterial,
  useGetInteractiveMaterial1,
} from "@packages/api/madlenAPI";
import { InteractiveMaterialResponseDtoStatus } from "@packages/api/madlenAPI.schemas";
import WelcomeTab from "../../../components/interactive-materials/WelcomeTab";
import { useQueryClient } from "@tanstack/react-query";
import { Text, Button } from "@core/index";
import LoadingContainer from "@packages/components/Loading";
import { useToast } from "@packages/contexts/ToastContext";
import { useNavigate } from "react-router-dom";

interface ErrorStateProps {
  error: Error | null;
  onRetry: () => void;
}

const ErrorState = memo(({ error, onRetry }: ErrorStateProps) => {
  const { t } = useTranslation();

  return (
    <div className="min-h-[400px] flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="relative w-full max-w-lg">
        <div className="absolute inset-0 bg-rose-500/20 dark:bg-rose-500/10 blur-xl rounded-full" />
        <div className="relative bg-white/90 dark:bg-gray-800/90 rounded-2xl p-6 sm:p-8 shadow-xl border border-rose-100 dark:border-rose-900">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="p-4 bg-rose-100 dark:bg-rose-900/50 rounded-full animate-bounce-slow">
              <AlertTriangle
                size={32}
                className="text-rose-600 dark:text-rose-400"
              />
            </div>
            <Text variant="h2" className="!text-rose-600 dark:!text-rose-400">
              {t("interactive_material.error.loadingError")}
            </Text>
            <Text variant="p" status="subtle" size="base" className="max-w-md">
              {error?.message ||
                t("interactive_material.error.unexpectedError")}
            </Text>
            <Button
              size="md"
              bgColor="bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/50 dark:hover:bg-rose-900/70"
              rounded="lg"
              textColor="text-rose-600 dark:text-rose-400"
              onClick={onRetry}
              className="group flex items-center gap-2"
            >
              <RefreshCcw
                size={16}
                className="group-hover:rotate-180 transition-transform duration-500"
              />
              {t("common.retry")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

interface CloneViewProps {
  id: string;
}

const CloneView = ({ id }: CloneViewProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [cloneLoading, setCloneLoading] = useState(false);
  const { addToast } = useToast();

  const {
    data: material,
    isLoading,
    error,
    refetch,
  } = useGetInteractiveMaterial1(Number(id), {
    query: {
      refetchInterval: (query) => {
        if (
          query.state.data?.status ===
          InteractiveMaterialResponseDtoStatus.INITIALIZING
        ) {
          return 5000;
        }
        return false;
      },
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    },
  });

  const { mutateAsync: cloneInteractiveMaterial } = useCloneInteractiveMaterial(
    {
      mutation: {
        onMutate: () => {
          setCloneLoading(true);
        },
        onSuccess: (data) => {
          setCloneLoading(false);
          queryClient.invalidateQueries({
            queryKey: getGetInteractiveMaterialQueryKey(Number(id)),
          });
          addToast({
            title: t("interactive_material.clone.success"),
            description: t("interactive_material.clone.successDescription"),
            variant: "success",
            duration: 2000,
          });
          setTimeout(() => {
            navigate(`/content/interactive-materials/${data.id}/edit`);
          }, 2000);
        },
        onError: () => {
          setCloneLoading(false);
          addToast({
            title: t("interactive_material.clone.error"),
            description: t("interactive_material.clone.errorDescription"),
            variant: "error",
          });
        },
        onSettled: () => {
          setCloneLoading(false);
        },
      },
    },
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rose-50 dark:from-gray-900 dark:to-rose-900">
      <div className="container max-w-7xl mx-auto px-4 py-4 sm:py-8">
        {isLoading ? (
          <LoadingContainer
            title={t("interactive_material.loading.title")}
            themeColor="rose"
            fullHeight={false}
          />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : material ? (
          <div className="space-y-6 sm:space-y-8">
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-lg rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700/50">
              <WelcomeTab
                material={material}
                clone={true}
                cloneLoading={cloneLoading}
                cloneHandler={() => {
                  cloneInteractiveMaterial({ id: Number(material.id) });
                }}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CloneView;
